import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { type EmergencyCreditStatsCommon } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { CancellationCreditProgressBar } from "@src/appV2/redesign/EmergencyCredits/components/CancellationCreditProgressBar";

import { CancellationScoreDisplay } from "./components/CancellationScoreDisplay";
import { RestrictedBadge } from "./components/RestrictedBadge";

export interface ShiftCancelPageConfirmedData {
  finalScore: number;
  isWithinECWindow: boolean;
  finalECStats?: EmergencyCreditStatsCommon;
  isBecomingRestricted: boolean;
  isAlreadyRestricted: boolean;
  isFinalRestriction: boolean;
  reEnrollmentScore: number | undefined;
}

export interface ShiftCancelPageConfirmedHandlers {
  onContinue: () => void;
  isLoading?: boolean;
}

type ShiftCancelPageConfirmationScreenProps = Omit<
  ShiftCancelPageConfirmedData,
  "isFinalRestriction" | "reEnrollmentScore"
> &
  ShiftCancelPageConfirmedHandlers;

export function ShiftCancelPageConfirmationScreen(
  props: ShiftCancelPageConfirmationScreenProps
): JSX.Element {
  const {
    finalScore,
    isWithinECWindow,
    finalECStats,
    isBecomingRestricted,
    isAlreadyRestricted,
    isLoading = false,
    onContinue,
  } = props;

  const isRestricted = isBecomingRestricted || isAlreadyRestricted;

  const showCreditProgress =
    !isRestricted &&
    isWithinECWindow &&
    isDefined(finalECStats) &&
    finalECStats.totalAvailableCredits === 0;

  return (
    <Stack textAlign="center" flex={1}>
      <Stack
        flex={1}
        spacing={12}
        sx={{
          marginTop: "64px !important",
          paddingX: 4,
        }}
      >
        <CancellationScoreDisplay
          animated
          score={finalScore}
          isRestricted={isRestricted}
          isBecomingRestricted={isBecomingRestricted}
        >
          {isRestricted ? (
            <Stack alignItems="center" sx={{ marginTop: "56px !important" }}>
              <RestrictedBadge />
            </Stack>
          ) : null}
          {showCreditProgress && (
            <CancellationCreditProgressBar cancellationCreditStats={finalECStats} />
          )}
        </CancellationScoreDisplay>
      </Stack>

      <DialogFooter orientation="vertical">
        <LoadingButton
          fullWidth
          isLoading={isLoading}
          variant="contained"
          size="large"
          onClick={onContinue}
        >
          {isBecomingRestricted ? "Next" : "Got it"}
        </LoadingButton>
      </DialogFooter>
    </Stack>
  );
}
